import React from 'react';
import type {
  PaginationMeta,
  PublicQuestionListItem as PublicQuestionListItemData,
} from '@find-my-part/types';
import { useStyleInjection, useOnlineStatus } from '../../hooks';
import { PublicQuestionListItem } from './PublicQuestionListItem';
import { PublicQuestionListSkeleton } from './PublicQuestionListSkeleton';
import { Pagination } from '../Pagination';
import { OfflineNotice } from '../OfflineNotice';
import { BREAKPOINTS } from '../../tokens';

export interface PublicQuestionListProps {
  questions: PublicQuestionListItemData[];
  pagination: PaginationMeta | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  onPageChange?: (page: number) => void;
  className?: string;
}

const USER_FRIENDLY_ERRORS: Record<string, string> = {
  RATE_LIMITED: 'Too many requests. Please wait a moment.',
  NETWORK_ERROR: 'Unable to connect. Please check your connection.',
  OFFLINE: "You're currently offline. Please check your connection.",
  SERVER_ERROR: 'Something went wrong. Please try again.',
  REQUEST_TIMEOUT: 'The request took too long. Please try again.',
  MAX_RETRIES_EXCEEDED: 'Unable to reach the server. Please try again later.',
  WIDGET_INIT_ERROR: 'Unable to load questions. Please refresh the page.',
};

function sanitizeError(error: string): string {
  for (const [code, message] of Object.entries(USER_FRIENDLY_ERRORS)) {
    if (error.includes(code) || error.toLowerCase().includes(code.toLowerCase())) {
      return message;
    }
  }
  return 'Something went wrong. Please try again.';
}

export function PublicQuestionList({
  questions,
  pagination,
  isLoading,
  error,
  refetch,
  onPageChange,
  className = '',
}: PublicQuestionListProps): React.ReactElement {
  useStyleInjection(browseStyles, 'fmp-browse-styles');
  const isOnline = useOnlineStatus();

  if (isLoading) {
    return (
      <div className={`fmp-browse ${className}`}>
        <PublicQuestionListSkeleton count={5} />
      </div>
    );
  }

  if (error) {
    return (
      <div className={`fmp-browse ${className}`}>
        {!isOnline ? (
          <OfflineNotice />
        ) : (
          <div className="fmp-browse__error" role="alert">
            <p className="fmp-browse__error-message">{sanitizeError(error)}</p>
          </div>
        )}
        <div className="fmp-browse__error-actions">
          <button
            type="button"
            onClick={refetch}
            className="fmp-btn fmp-btn--secondary"
            aria-label="Retry loading questions"
            disabled={!isOnline}
          >
            {isOnline ? 'Try Again' : 'Waiting for connection...'}
          </button>
        </div>
      </div>
    );
  }

  if (questions.length === 0) {
    return (
      <div className={`fmp-browse ${className}`}>
        {pagination && onPageChange && pagination.totalPages > 1 && (
          <>
            <p className="fmp-browse__page-empty" role="status">
              No answered cases appear on this page.
            </p>
            <Pagination
              currentPage={pagination.page}
              totalPages={pagination.totalPages}
              onPageChange={onPageChange}
            />
          </>
        )}
      </div>
    );
  }

  return (
    <div className={`fmp-browse ${className}`}>
      <ul className="fmp-browse-list" role="list">
        {questions.map((question) => (
          <PublicQuestionListItem
            key={question.questionId}
            question={question}
          />
        ))}
      </ul>
      {pagination && onPageChange && (
        <Pagination
          currentPage={pagination.page}
          totalPages={pagination.totalPages}
          onPageChange={onPageChange}
        />
      )}
    </div>
  );
}

const browseStyles = `
  :where(.fmp-widget-scope) .fmp-browse {
    width: 100%;
  }

  :where(.fmp-widget-scope) .fmp-browse__page-empty {
    margin: 0;
    color: var(--fmp-color-text-muted);
    line-height: var(--fmp-line-height-body);
  }

  :where(.fmp-widget-scope) .fmp-browse-list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: var(--fmp-space-sm);
  }

  :where(.fmp-widget-scope) .fmp-browse-item {
    background-color: var(--fmp-color-surface);
    border: var(--fmp-border-width) solid var(--fmp-color-border);
    border-radius: var(--fmp-radius-surface);
    box-shadow: var(--fmp-shadow-resting);
    transition: box-shadow var(--fmp-duration-normal) var(--fmp-ease-standard);
  }

  :where(.fmp-widget-scope) .fmp-browse-item--clickable {
    overflow: hidden;
  }

  :where(.fmp-widget-scope) .fmp-browse-item--clickable:hover {
    border-color: var(--fmp-color-primary);
    box-shadow: var(--fmp-shadow-raised);
  }

  :where(.fmp-widget-scope) .fmp-browse-item__link {
    display: grid;
    grid-template-columns: var(--fmp-size-96) minmax(0, 1fr);
    gap: var(--fmp-space-lg);
    min-height: var(--fmp-size-96);
    padding: var(--fmp-space-lg);
    color: inherit;
    text-decoration: none;
  }

  :where(.fmp-widget-scope) .fmp-browse-item__link:focus-visible {
    outline: var(--fmp-outline-width) var(--fmp-outline-style) var(--fmp-color-primary);
    outline-offset: var(--fmp-outline-inset-offset);
  }

  :where(.fmp-widget-scope) .fmp-browse-item__media {
    width: var(--fmp-size-96);
    aspect-ratio: 1;
    overflow: hidden;
    border-radius: var(--fmp-radius-control);
    background: var(--fmp-color-surface-muted);
  }

  :where(.fmp-widget-scope) .fmp-browse-item__thumbnail {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  :where(.fmp-widget-scope) .fmp-browse-item__placeholder {
    display: grid;
    width: 100%;
    height: 100%;
    place-items: center;
    color: var(--fmp-color-text-hint);
  }

  :where(.fmp-widget-scope) .fmp-browse-item__placeholder svg {
    width: var(--fmp-size-32);
    height: var(--fmp-size-32);
  }

  :where(.fmp-widget-scope) .fmp-browse-item__content {
    display: flex;
    flex-direction: column;
    gap: var(--fmp-space-sm);
  }

  :where(.fmp-widget-scope) .fmp-browse-item__header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: var(--fmp-space-md);
  }

  :where(.fmp-widget-scope) .fmp-browse-item__title {
    margin: 0;
    font-size: var(--fmp-font-size-base);
    font-weight: var(--fmp-font-weight-semibold);
    color: var(--fmp-color-text);
    line-height: var(--fmp-line-height-body);
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }

  :where(.fmp-widget-scope) .fmp-browse-item__description {
    margin: 0;
    font-size: var(--fmp-font-size-base);
    color: var(--fmp-color-text-muted);
    line-height: var(--fmp-line-height-body);
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }

  :where(.fmp-widget-scope) .fmp-browse-item__meta {
    display: flex;
    align-items: center;
    gap: var(--fmp-space-sm);
    font-size: var(--fmp-font-size-sm);
    color: var(--fmp-color-text-muted);
    flex-wrap: wrap;
  }

  :where(.fmp-widget-scope) .fmp-browse-item__separator {
    color: var(--fmp-color-separator);
  }

  :where(.fmp-widget-scope) .fmp-browse-item__author {
    font-weight: var(--fmp-font-weight-semibold);
    color: var(--fmp-color-text-hint);
  }

  :where(.fmp-widget-scope) .fmp-question-status-badge {
    display: inline-flex;
    align-items: center;
    gap: var(--fmp-space-xs);
    padding: var(--fmp-space-xs) var(--fmp-size-10);
    border-radius: var(--fmp-radius-pill);
    font-size: var(--fmp-font-size-xs);
    font-weight: var(--fmp-font-weight-semibold);
    white-space: nowrap;
    background-color: var(--fmp-color-surface-muted);
    color: var(--fmp-color-text-secondary);
    flex-shrink: 0;
  }

  :where(.fmp-widget-scope) .fmp-question-status-badge--success {
    background-color: var(--fmp-color-success-bg);
    color: var(--fmp-color-success);
  }

  :where(.fmp-widget-scope) .fmp-question-status-badge--info {
    background-color: var(--fmp-color-info-bg);
    color: var(--fmp-color-info);
  }

  :where(.fmp-widget-scope) .fmp-question-status-badge--warning {
    background-color: var(--fmp-color-warning-bg);
    color: var(--fmp-color-warning-text);
  }

  :where(.fmp-widget-scope) .fmp-question-status-badge--danger {
    background-color: var(--fmp-color-danger-bg);
    color: var(--fmp-color-danger);
  }

  :where(.fmp-widget-scope) .fmp-question-status-badge__icon {
    width: var(--fmp-size-14);
    height: var(--fmp-size-14);
  }

  :where(.fmp-widget-scope) .fmp-browse__error {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: var(--fmp-space-section) var(--fmp-space-xl);
    text-align: center;
  }

  :where(.fmp-widget-scope) .fmp-browse__error-message {
    margin: 0;
    font-size: var(--fmp-font-size-base);
    color: var(--fmp-color-error);
  }

  :where(.fmp-widget-scope) .fmp-browse__error-actions {
    display: flex;
    justify-content: center;
    margin-top: var(--fmp-space-lg);
  }

  @container fmp-browse (width < ${BREAKPOINTS.container.compact}) {
    :where(.fmp-widget-scope) .fmp-browse-item__link {
      grid-template-columns: var(--fmp-size-64) minmax(0, 1fr);
      gap: var(--fmp-space-md);
      padding: var(--fmp-size-14);
    }

    :where(.fmp-widget-scope) .fmp-browse-item__media {
      width: var(--fmp-size-64);
    }

    :where(.fmp-widget-scope) .fmp-browse-item__header {
      flex-direction: column;
      gap: var(--fmp-space-sm);
    }

    :where(.fmp-widget-scope) .fmp-browse-item__title {
      font-size: var(--fmp-font-size-base);
    }

    :where(.fmp-widget-scope) .fmp-browse-item__meta {
      font-size: var(--fmp-font-size-xs);
      gap: var(--fmp-size-6);
    }

    :where(.fmp-widget-scope) .fmp-browse-item__description {
      -webkit-line-clamp: 1;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    :where(.fmp-widget-scope) .fmp-browse-item {
      transition: none;
    }
  }

  @media (prefers-contrast: high) {
    :where(.fmp-widget-scope) .fmp-browse-item__link:focus-visible {
      outline-color: currentColor;
    }

    :where(.fmp-widget-scope) .fmp-question-status-badge {
      border: var(--fmp-border-width) solid currentColor;
    }
  }
`;
